<template>
  <form @submit.prevent="$emit('confirm')" class="coupon-form">
    <div v-if="newEntry" class="flex flex-col gap-4">
      <section class="form-section">
        <h3>Coupon</h3>
        <div class="grid grid-cols-2 gap-4">
          <BaseTextField
            v-model="newEntry.code"
            id="code"
            type="text"
            label="Code"
            :disabled="!create"
            required
          />
          <BaseSelectField
            v-model="newEntry.status"
            :options="[
              { name: 'Active', code: 1 },
              { name: 'Inactive', code: 0 }
            ]"
            label="Status"
            required
          />
          <BaseTextField
            v-model="newEntry.fromDate"
            type="date"
            label="From date"
            required
          />
          <BaseTextField
            v-model="newEntry.toDate"
            type="date"
            label="To date"
            required
          />
        </div>
      </section>

      <section class="form-section">
        <h3>Discount</h3>
        <div class="grid grid-cols-2 gap-4">
          <BaseSelectField
            v-model="newEntry.discountType"
            :disabled="!create"
            :options="discountTypeOptions"
            label="Discount type"
            required
          />
          <BaseTextField
            v-if="['percentage', 'fixed'].includes(newEntry.discountType)"
            v-model="newEntry.amount"
            type="number"
            step="0.01"
            :label="newEntry.discountType === 'percentage' ? 'Percent (%)' : 'Amount ($)'"
            required
          />
        </div>
        <p
          v-if="newEntry.discountType === 'product'"
          class="mt-2 text-sm text-blue-700 bg-blue-50 rounded p-2"
        >
          Each product in the cart gets the discount of the most specific rule
          that covers it: product, then subcategory, then whole category.
          {{
            create
              ? "You will add the rules right after creating the coupon."
              : "Manage them in the Products and Categories tabs."
          }}
        </p>
        <div class="grid grid-cols-2 gap-4 mt-2">
          <BaseSelectField
            v-model="newEntry.freeShipping"
            label="Free shipping"
            :options="[
              { name: 'Select', code: '' },
              { name: 'Yes', code: '1' },
              { name: 'No', code: '0' }
            ]"
            required
          />
        </div>
      </section>

      <section class="form-section">
        <h3>Limits</h3>
        <div class="grid grid-cols-2 gap-4">
          <BaseTextField
            v-model="newEntry.minAmount"
            type="number"
            step="0.01"
            label="Min order amount (0 = none)"
            required
          />
          <BaseTextField
            v-model="newEntry.maxAmount"
            type="number"
            step="0.01"
            label="Max order amount (0 = none)"
            required
          />
          <BaseTextField
            v-model="newEntry.usesLimit"
            type="number"
            label="Total uses (0 = unlimited)"
            required
          />
          <BaseTextField
            v-model="newEntry.usesLimitUser"
            type="number"
            label="Uses per customer (0 = unlimited)"
            required
          />
          <div class="col-span-2">
            <BaseTextField
              v-model="newEntry.conditions"
              type="text"
              label="Conditions / internal note"
            />
          </div>
        </div>
      </section>
    </div>
    <div class="flex justify-end gap-2 mt-4">
      <template v-if="saving">
        Saving ...
      </template>
      <template v-else>
        <button type="button" class="btn btn-dark-button" @click="$emit('discard')">
          Discard
        </button>
        <button
          type="submit"
          class="btn btn-success-button"
          :disabled="!(changed || create)"
        >
          {{ create ? "Create coupon" : "Save changes" }}
        </button>
      </template>
    </div>
  </form>
</template>
<script>
import { COUPON_DISCOUNT_TYPES } from "./couponLabels.js";

export default {
  name: "CouponForm",
  props: {
    value: {
      type: Object,
      required: true
    },
    create: {
      type: Boolean,
      default: false
    },
    changed: {
      type: Boolean
    },
    saving: {
      type: Boolean
    }
  },
  data() {
    return {
      newEntry: null
    };
  },
  computed: {
    discountTypeOptions() {
      return [{ name: "Select discount type", code: "" }].concat(
        COUPON_DISCOUNT_TYPES
      );
    }
  },
  mounted() {
    this.newEntry = {
      id: this.value.id || null,
      code: this.value.code,
      fromDate: this.value.fromDate,
      toDate: this.value.toDate,
      minAmount: this.value.minAmount,
      maxAmount: this.value.maxAmount,
      discountType: this.value.discountType,
      amount: this.value.amount,
      freeShippingType: this.value.freeShippingType,
      usesLimit: this.value.usesLimit,
      usesLimitUser: this.value.usesLimitUser,
      freeShipping: this.value.freeShipping,
      status: this.value.status,
      products: this.value.products,
      conditions: this.value.conditions || ""
    };
  },
  watch: {
    newEntry: {
      handler(val) {
        this.$emit("input", val);
      },
      deep: true
    }
  }
};
</script>
<style lang="scss" scoped>
.form-section {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  h3 {
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #374151;
  }
}
</style>
